/**
 * Générateur — Il identifie des rotations et des homothéties dans des
 * frises, des pavages et des rosaces.
 * Attendu ID : 241 | Niveau : 3ème | Domaine : Géométrie plane (d17, O4)
 *
 * Source doc prof (séances D2S1-D2S2 + Créé n°17). Plan auto-validé
 * 2026-07-18 (TODO §6) : « rosace » (angle élémentaire 360/n et rotation
 * entre motifs numérotés), « frise » (motif debout / tête en bas :
 * translation ou rotation de 180° selon la parité de l'écart), « pavage »
 * (n motifs autour d'un nœud → k × 360/n), « scratch » (le pont
 * algorithmique ↔ aires : répéter n fois / tourner 360/n, conservation des
 * aires). Différence avec 236 (thème #9) : ici on IDENTIFIE la
 * transformation dans le motif répété, 236 travaille ses effets.
 */

import { randomChoice, randInt, makeQcm, makeTexteTrou } from '../../../utils';

const ATTENDU_ID = 241;
const L_ROSACE = 'Rotations dans une rosace';
const L_FRISE = 'Quelle transformation dans la frise ?';
const L_PAVAGE = 'Rotations dans un pavage';
const L_SCRATCH = 'Le script du pavage (et son aire)';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « rosace » ═════════

const genRosaceTrous = () => {
    const n = randomChoice([5, 8, 9, 10, 12]);
    const elem = 360 / n;
    const k = randInt(2, Math.floor(n / 2));                // k pas → angle ≤ 180°
    const centre = randomChoice(['A', 'O', 'R']);
    const i = randInt(1, n - k);
    const phrase = `$\\text{Angle élémentaire : } {{${elem}}}° \\qquad`
        + ` \\text{rotation du motif ${i} au motif ${i + k} : } {{${k * elem}}}°$`;
    const payload = makeTexteTrou(phrase,
        // « ils remplissent le tour complet » est une DONNÉE (sans elle
        // l'exercice n'a pas de solution) : elle passe en phrase.
        `<p>Une rosace est formée de ${n} motifs identiques numérotés de 1 à ${n}, régulièrement répartis `
        + `autour d'un point ${centre} et remplissant le tour complet. Une rotation de centre ${centre} `
        + 'envoie chaque motif sur son voisin, dans le sens des numéros.</p>'
        + `<p><b>Quel est l'angle élémentaire de la rosace, puis l'angle de la rotation de centre ${centre} `
        + `qui envoie le motif ${i} sur le motif ${i + k} ?</b></p>`,
        { ...opts(L_ROSACE), mode: 'saisie' });
    payload._meta.params = { n, k, i, centre };
    return payload;
};

// ═════════ « frise » ═════════

const CHOIX_FRISE = [
    'Une translation',
    // « (symétrie centrale) » n'était donné que sur ce choix : la glose le
    // singularisait alors qu'il est la bonne réponse une fois sur deux.
    'Une rotation de 180°',
    'Une rotation de 90°',
    'Aucune de ces transformations',
];

const genFriseQcm = () => {
    const i = randInt(1, 5);
    const k = randInt(1, 3);
    const j = i + k;
    const habillage = randomChoice(['papier peint', 'carrelage d\'une salle de bains', 'bordure de cahier', 'tissu brodé']);
    const correct = k % 2 === 0 ? CHOIX_FRISE[0] : CHOIX_FRISE[1];
    const payload = makeQcm([...CHOIX_FRISE], correct,
        `<p>Une frise — ${habillage} — est fabriquée en répétant un motif M : les motifs 1, 2, 3, 4, 5… `
        + 'alternent motif DEBOUT aux numéros impairs et motif TÊTE EN BAS aux numéros pairs, '
        + 'régulièrement espacés sur une même ligne.</p>'
        + `<p><b>Quelle transformation envoie le motif ${i} sur le motif ${j} ?</b></p>`,
        opts(L_FRISE));
    payload._meta.params = { i, j, k };
    return payload;
};

// ═════════ « pavage » ═════════

const genPavageQcm = () => {
    const n = randomChoice([3, 4, 6]);
    const elem = 360 / n;
    const k = randInt(1, n - 1);
    const noeud = randomChoice(['A', 'B', 'D', 'P']);
    const i = randInt(1, n - k);
    const correct = `${k * elem}°`;
    const distracteurs = [...new Set([elem, 2 * elem, 3 * elem, 180, 90, 45].map(a => `${a}°`))]
        .filter(c => c !== correct).slice(0, 3);
    const payload = makeQcm([correct, ...distracteurs], correct,
        `<p>Dans un pavage, ${n} motifs identiques numérotés de 1 à ${n} se répartissent régulièrement `
        + `autour du nœud ${noeud} et remplissent le tour complet. Une rotation de centre ${noeud} `
        + 'envoie chaque motif sur son voisin, dans le sens des numéros.</p>'
        + `<p><b>Quel est l'angle de la rotation de centre ${noeud} qui envoie le motif ${i} sur le `
        + `motif ${i + k} ?</b></p>`,
        opts(L_PAVAGE));
    payload._meta.params = { n, k, i, noeud };
    return payload;
};

// ═════════ « scratch » — le pont algorithmique ↔ aires ═════════

const genScratchTrous = () => {
    const n = randomChoice([3, 4, 6]);
    const aire = randInt(5, 14);
    const phrase = `répéter {{${n}}} fois ; tourner ↻ de {{${360 / n}}} degrés ; aire totale : {{${n * aire}}} carreaux.`;
    const payload = makeTexteTrou(phrase,
        `<p>Un script Scratch trace un motif d'aire ${aire} carreaux, puis construit une figure complète en `
        + `répétant : [Motif ; tourner ↻ de … degrés]. La figure obtenue est formée de ${n} motifs, `
        + 'rotations successives du premier, qui font exactement le tour d\'un point.</p>'
        // « les rotations CONSERVENT les aires » est la propriété que l'exercice
        // fait précisément mobiliser pour éviter de compter les carreaux.
        + '<p><b>Complète le script, puis donne l\'aire totale de la figure SANS compter '
        + 'les carreaux.</b></p>',
        { ...opts(L_SCRATCH), mode: 'saisie' });
    payload._meta.params = { n, aire };
    return payload;
};

const GENERATORS = {
    rosace_texte_trou_saisie: genRosaceTrous,
    frise_qcm_unique: genFriseQcm,
    pavage_qcm_unique: genPavageQcm,
    scratch_texte_trou_saisie: genScratchTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'rosace', label: L_ROSACE, formatsList: buildFormats('rosace') },
    { id: 'frise', label: L_FRISE, formatsList: buildFormats('frise') },
    { id: 'pavage', label: L_PAVAGE, formatsList: buildFormats('pavage') },
    { id: 'scratch', label: L_SCRATCH, formatsList: buildFormats('scratch') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
